import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import { CatalogSearchField } from "../../components/ui/CatalogCard";
import {
  EmptyState,
  PageHeader,
  RowActions,
  StatusBadge,
} from "../../components/ui";
import { useAttendancePeriod } from "../../hooks/useAttendancePeriod";
import { useDebounce } from "../../hooks/useDebounce";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  ATTENDANCE_EXPORT_HARD_LIMIT,
  ATTENDANCE_SUCCESS_TOAST_DURATION_MS,
  Attendance,
  AttendanceSortDirection,
  AttendanceSortField,
} from "../../models/attendance";
import { BenefitsMetricsTenantScopeHeader, BenefitsMetricsRefreshingIndicator } from "../Reporting/components/BenefitsMetricsWidgets";
import { PersonCell } from "./components/PersonCell";
import { AttendanceHistoryDialog } from "./components/AttendanceHistoryDialog";
import { showExportQueuedToast } from "./components/ExportQueuedToast";
import {
  useLazyExportAttendancesQuery,
  useGetAttendanceEventOptionsQuery,
  useGetAttendancesQuery,
} from "../../store/services/attendances";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";
import { downloadBlob } from "../../utils/functions/downloadBlob";
import { getApiErrorMessage, getApiErrorCode } from "../../utils/functions/api-errors";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import "./attendances.css";

const DEFAULT_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const isPresentOptions = [
  { label: "Todos", value: null },
  { label: "Sí", value: true },
  { label: "No", value: false },
];

const toApiDate = (date: Date): string => format(date, "yyyy-MM-dd");

export const Attendances = () => {
  const scope = useTenantScope();
  const selectedTenantId = scope.tenantId;

  const [filtersTenantId, setFiltersTenantId] = useState(selectedTenantId);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  // GTK-748 — el filtro de evento es el select del diseño aprobado. Lo que se
  // escribe en su buscador viaja como `search` a /attendance/options/events, que
  // resuelve nombre o id igual que antes; al elegir, la tabla se acota por
  // `event_id`, que es más exacto que el nombre libre.
  const [selectedEvent, setSelectedEvent] = useState<{ id: number; name: string } | null>(null);
  const eventId = selectedEvent?.id ?? null;
  const [eventSearch, setEventSearch] = useState("");
  const debouncedEventSearch = useDebounce(eventSearch, SEARCH_DEBOUNCE_MS);
  const trimmedEventSearch = debouncedEventSearch.trim();
  const [isPresent, setIsPresent] = useState<boolean | null>(null);
  const attendancePeriod = useAttendancePeriod();
  const [sortBy, setSortBy] = useState<AttendanceSortField>("marked_at");
  const [sortDir, setSortDir] = useState<AttendanceSortDirection>("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [historyAttendance, setHistoryAttendance] = useState<Attendance | null>(null);

  if (filtersTenantId !== selectedTenantId) {
    setFiltersTenantId(selectedTenantId);
    setEventSearch("");
    setSelectedEvent(null);
    setPage(1);
    setHistoryAttendance(null);
  }

  const { enabled: isSectionFlagEnabled, loading: isLoadingSectionFlag } =
    useIsFeatureFlagEnabled("attendance_bo_section_enabled");
  const isAllTenantsScope =
    !scope.loading && scope.role === "SUPER_ADMIN" && selectedTenantId === null;
  const canViewAttendances = isAllTenantsScope || isSectionFlagEnabled;
  const isResolvingAccess = scope.loading || (!isAllTenantsScope && isLoadingSectionFlag);

  const trimmedSearch = debouncedSearch.trim();
  // Sin presets, el rango lo definen las dos fechas: alcanza con mirarlas para
  // saber si el vacío de la tabla es "no hay datos" o "los filtros los excluyen".
  const hasActiveFilters =
    trimmedSearch.length > 0 ||
    eventId !== null ||
    isPresent !== null ||
    attendancePeriod.dateFrom !== null ||
    attendancePeriod.dateTo !== null;

  const listFilters = {
    tenantId: selectedTenantId,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(eventId !== null ? { eventId } : {}),
    ...(isPresent !== null ? { isPresent } : {}),
    ...(attendancePeriod.dateFrom ? { dateFrom: toApiDate(attendancePeriod.dateFrom) } : {}),
    ...(attendancePeriod.dateTo ? { dateTo: toApiDate(attendancePeriod.dateTo) } : {}),
    sortBy,
    sortDir,
  };

  const skipQueries = !canViewAttendances || scope.loading;

  const { currentData: attendancePage, isLoading, isFetching, isError } = useGetAttendancesQuery(
    { ...listFilters, page, perPage },
    { skip: skipQueries }
  );

  const { currentData: eventOptionsData } = useGetAttendanceEventOptionsQuery(
    { search: trimmedEventSearch, tenantId: selectedTenantId, purpose: "history" },
    { skip: skipQueries }
  );
  const availableEvents = eventOptionsData ?? [];
  const eventsWithSelection = selectedEvent && !availableEvents.some((event) => event.id === selectedEvent.id)
    ? [selectedEvent, ...availableEvents]
    : availableEvents;
  const eventOptions = eventsWithSelection.map((event) => ({
    label: `${event.name} (#${event.id})`,
    value: event.id,
  }));

  const [exportAttendances, { isFetching: isExporting }] =
    useLazyExportAttendancesQuery({ refetchOnFocus: false, refetchOnReconnect: false });

  const handleExport = async () => {
    const request = exportAttendances(listFilters);
    try {
      const result = await request.unwrap();
      if (result.status === 202) {
        showExportQueuedToast();
        return;
      }
      if (result.status === 204) {
        toast("No hay asistencias para los filtros seleccionados");
        return;
      }
      downloadBlob(result.blob, result.filename);
      toast.success("CSV exportado", { duration: ATTENDANCE_SUCCESS_TOAST_DURATION_MS, icon: null });
    } catch (error) {
      toast.error(getApiErrorCode(error) === "EXPORT_LIMIT_EXCEEDED"
        ? "Hay más de 10.000 registros. Usá filtros para reducir el rango."
        : getApiErrorMessage(error) ?? "No se pudo exportar el CSV");
    } finally {
      request.unsubscribe();
    }
  };

  const handleExportClick = () => {
    const total = attendancePage?.total ?? 0;

    if (total > ATTENDANCE_EXPORT_HARD_LIMIT) {
      toast.error("Hay más de 10.000 registros. Usá filtros para reducir el rango.");
      return;
    }

    handleExport();
  };

  const handlePage = (event: DataTablePageEvent) => {
    setPage((event.page ?? 0) + 1);
    setPerPage(event.rows);
  };

  const handleSort = (event: DataTableSortEvent) => {
    const field = event.sortField as AttendanceSortField | undefined;
    if (!field || event.sortOrder === 0 || event.sortOrder === null) {
      setSortBy("marked_at");
      setSortDir("desc");
      setPage(1);
      return;
    }
    setSortBy(field);
    setSortDir(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setEventSearch("");
    setSelectedEvent(null);
    setIsPresent(null);
    attendancePeriod.reset();
    setPage(1);
  };

  const changeDateFrom = (date: Date | null) => {
    attendancePeriod.handleDateFromChange(date);
    setPage(1);
  };

  const changeDateTo = (date: Date | null) => {
    attendancePeriod.handleDateToChange(date);
    setPage(1);
  };

  const personTemplate = (row: Attendance) => (
    <PersonCell
      name={row.personName}
      avatarUrl={row.personAvatarUrl}
      secondaryLine={row.isCompanion ? "Acompañante" : null}
    />
  );

  const hostTemplate = (row: Attendance) => (
    <PersonCell name={row.hostName} avatarUrl={row.hostAvatarUrl} />
  );

  const header = <PageHeader title="Registro de Asistencia" />;

  if (isResolvingAccess) {
    return (
      <div className="p-card attendances-page">
        {header}
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (!canViewAttendances) {
    return (
      <div className="p-card attendances-page">
        {header}
        <div className="flex justify-content-center p-4">
          <Message severity="info" text="La sección está deshabilitada para este tenant." />
        </div>
      </div>
    );
  }

  const filtersHeader = (
    <div className="flex flex-wrap align-items-end gap-3">
      <div style={{ flex: "1 1 240px" }}>
        <CatalogSearchField
          inputId="attendanceSearch"
          label="Usuario / Email"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Buscar por nombre o email"
        />
      </div>

      <div className="flex flex-column" style={{ flex: "1 1 240px", gap: "var(--space-6)" }}>
        <label htmlFor="attendanceEvent" className="text-600">
          Evento
        </label>
        <Dropdown
          inputId="attendanceEvent"
          value={eventId}
          options={eventOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Todos los eventos"
          filter
          showClear
          filterPlaceholder="Nombre o ID del evento"
          emptyFilterMessage="No se encontraron eventos"
          emptyMessage="No hay eventos todavía"
          disabled={isLoading}
          onFilter={(event) => setEventSearch(event.filter)}
          onChange={(event) => {
            setSelectedEvent(eventsWithSelection.find((option) => option.id === event.value) ?? null);
            setPage(1);
          }}
          className="w-full"
        />
      </div>

      {/* §6.5 — el datepicker va en español; el tope es hoy, no hay asistencias
          en el futuro. El orden desde/hasta lo corrige `useAttendancePeriod`. */}
      <div className="attendances-date-range">
        <label htmlFor="attendanceDateFrom" className="text-600">
          Rango de fechas
        </label>
        <div className="flex align-items-center gap-2">
          <Calendar
            inputId="attendanceDateFrom"
            value={attendancePeriod.dateFrom}
            onChange={(event) =>
              changeDateFrom(
                Array.isArray(event.value) ? event.value[0] ?? null : event.value ?? null
              )
            }
            dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
            locale={CALENDAR_LOCALE}
            placeholder="dd/mm/aaaa"
            maxDate={attendancePeriod.today}
            showIcon
            showButtonBar
            disabled={isLoading}
            className="w-full"
          />
          <span className="text-600">–</span>
          <Calendar
            inputId="attendanceDateTo"
            value={attendancePeriod.dateTo}
            onChange={(event) =>
              changeDateTo(
                Array.isArray(event.value) ? event.value[0] ?? null : event.value ?? null
              )
            }
            dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
            locale={CALENDAR_LOCALE}
            placeholder="dd/mm/aaaa"
            minDate={attendancePeriod.dateFrom ?? undefined}
            maxDate={attendancePeriod.today}
            showIcon
            showButtonBar
            disabled={isLoading}
            className="w-full"
          />
        </div>
      </div>

      {/* El diseño no lo dibuja, pero el backend filtra por is_present: sacarlo
          sería perder un filtro que ya existe. */}
      <div className="flex flex-column" style={{ flex: "0 1 170px", gap: "var(--space-6)" }}>
        <label htmlFor="attendanceIsPresent" className="text-600">
          Asistió
        </label>
        <Dropdown
          inputId="attendanceIsPresent"
          value={isPresent}
          options={isPresentOptions}
          optionLabel="label"
          optionValue="value"
          disabled={isLoading}
          className="w-full"
          onChange={(event) => {
            setIsPresent(event.value ?? null);
            setPage(1);
          }}
        />
      </div>

      <div className="flex flex-wrap align-items-end gap-2 ml-auto">
        {hasActiveFilters && (
          <Button
            icon="pi pi-filter-slash"
            rounded
            text
            severity="secondary"
            tooltip="Limpiar filtros"
            aria-label="Limpiar filtros"
            tooltipOptions={{ position: "top" }}
            onClick={resetFilters}
          />
        )}
        <Button
          label="Exportar CSV"
          icon="pi pi-download"
          outlined
          loading={isExporting}
          onClick={handleExportClick}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="p-card attendances-page neutral-input-controls">
        {header}

        <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

        <BenefitsMetricsRefreshingIndicator visible={isFetching && Boolean(attendancePage)} />
        {isError && <Message severity="error" text="No se pudieron cargar las asistencias." />}
        <DataTable
          key={selectedTenantId ?? "all"}
          header={filtersHeader}
          value={attendancePage?.items ?? []}
          loading={isLoading || (isFetching && !attendancePage)}
          dataKey="id"
          lazy
          paginator
          rows={perPage}
          first={(page - 1) * perPage}
          totalRecords={attendancePage?.total ?? 0}
          rowsPerPageOptions={[10, 20, 50]}
          onPage={handlePage}
          sortField={sortBy}
          sortOrder={sortDir === "asc" ? 1 : -1}
          onSort={handleSort}
          stripedRows
          scrollable
          scrollHeight="60vh"
          tableStyle={{ minWidth: "76rem" }}
          responsiveLayout="scroll"
          emptyMessage={<EmptyState entity="asistencias" />}
        >
          {scope.role === "SUPER_ADMIN" && (
            <Column
              field="tenant"
              header="Tenant"
              sortable
              body={(row: Attendance) => row.tenantName || row.tenantId}
              style={{ width: "12rem" }}
            />
          )}
          <Column
            field="event"
            header="Evento"
            sortable
            body={(row: Attendance) => (
              <Link to={`/events/${row.eventId}`} className="font-medium">
                {row.eventName}
              </Link>
            )}
            style={{ minWidth: "12rem" }}
          />
          <Column
            field="person"
            header="Persona"
            sortable
            body={personTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            field="email"
            header="Email"
            sortable
            body={(row: Attendance) => row.personEmail ?? "—"}
            style={{ minWidth: "14rem" }}
          />
          <Column
            field="host"
            header="Host"
            sortable
            body={hostTemplate}
            style={{ minWidth: "14rem" }}
          />
          <Column
            field="marked_at"
            header="Fecha/Hora"
            sortable
            body={(row: Attendance) => formatDateWithoutSeconds(row.markedAt)}
            style={{ width: "12rem" }}
          />
          <Column
            field="is_present"
            header="Asistió"
            sortable
            body={(row: Attendance) => (
              <StatusBadge
                label={row.isPresent ? "Sí" : "No"}
                tone={row.isPresent ? "success" : "error"}
              />
            )}
            style={{ width: "7rem" }}
          />
          <Column
            header="Acciones"
            body={(row: Attendance) => (
              <RowActions
                actions={[
                  {
                    icon: "pi pi-eye",
                    label: "Ver historial",
                    severity: "info",
                    visible: row.personUserId !== null,
                    onClick: () => setHistoryAttendance(row),
                  },
                ]}
              />
            )}
            style={{ width: "12rem" }}
          />
        </DataTable>
      </div>

      <AttendanceHistoryDialog
        attendance={historyAttendance}
        tenantId={selectedTenantId}
        onHide={() => setHistoryAttendance(null)}
      />
    </>
  );
};
